import {
  BidV2Status,
  CompetitionStageDto,
  ParticipationRequestStatus,
  SourcingEventStatus,
  type BuyerParticipationRequestDto,
  type BuyerParticipationRequestsDto,
  type EvalBidDto,
  type EvaluationDto,
  type NegotiationRoundV2Dto,
  type ReceivedBidV2Dto,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import {
  isQualificationScheme,
  isSchemeProcedure,
  isTwoStageProcedure,
} from "../../../sourcing-events/buyer/wizard/utils/procedure";
import { canSelectWinner } from "../award/canSelectWinner";
import { contractUrl } from "../contractUrl";
import type { EventTab } from "../EventTabs";
import type { EventSubTab } from "../useGoToEventTab";
import type { TranslationKey } from "../qualification/deriveQualificationSteps";
import { effectiveBidDeadline, effectiveRequestDeadline, isEvaluationLocked } from "../qualification/stage";
import { deriveMinimumRequirementReview } from "../minimum-requirements/deriveMinimumRequirementReview";
import { deriveTenderQualification } from "../qualification/tender/deriveTenderQualification";

// Every key this module can emit. They are looked up dynamically in NowPanel, so langen only sees
// them here:
// t('This procurement has been cancelled') t('This procurement is closed')
// t('The competition is open — deadline {{date}}') t('The competition is open')
// t('{{count}} requests received')
// t('{{count}} unanswered questions') t('Answer questions')
// t('{{count}} companies have registered interest')
// t('{{resolved}} of {{total}} suppliers qualified or rejected')
// t('Start the qualification review') t('Continue the qualification review')
// t('The deadline has passed — ready to start the evaluation') t('Begin evaluation')
// t('The system is open for applications') t('The system has expired') t('{{count}} applications awaiting assessment')
// t('Shortest deadline: {{count}} working days (FOA § 26-6)') t('Assess applications')
// t('Every request is reviewed — choose who to invite') t('Go to the qualification')
// t('{{reviewed}} of {{total}} tenders assessed against the minimum requirements')
// t('Start the minimum requirements review') t('Continue the minimum requirements review')
// t('The evaluation is complete — ready for award') t('Select a winner')
// t('{{reviewed}} of {{total}} tenders fully evaluated')
// t('Start the evaluation review') t('Continue the evaluation review')
// t('Send the award letters to start the standstill period') t('See the award')
// t('The standstill period has expired') t('Confirm award')
// t('The standstill period ends {{date}} ({{count}} days remaining)')
// t('The award is confirmed') t('Create contract') t('Go to contract')
export const NOW_STATE_KEYS = [
  "SCHEME_INTAKE",
  "REQUESTS_OPEN",
  "TENDER_OPEN",
  "REQUEST_QUALIFICATION",
  "REQUEST_SELECTION",
  "TENDER_QUALIFICATION",
  "START_EVALUATION",
  "MINIMUM_REQUIREMENTS",
  "EVALUATION",
  "READY_TO_AWARD",
  "AWARD_LETTERS_PENDING",
  "STANDSTILL_RUNNING",
  "STANDSTILL_EXPIRED",
  "AWARD_CONFIRMED",
  "CANCELLED",
  "CLOSED",
] as const;

export type NowStateKey = (typeof NOW_STATE_KEYS)[number];

export type NowActionTarget =
  | { kind: "tab"; tab: EventTab; subTab?: EventSubTab }
  | { kind: "url"; url: ReturnType<typeof contractUrl> };

export interface NowAction {
  labelKey: string;
  target: NowActionTarget;
}

export interface NowProgress {
  done: number;
  total: number;
}

export interface NowPanelState {
  key: NowStateKey;
  status: TranslationKey & { dateIso?: string };
  detail: TranslationKey[];
  progress: NowProgress | null;
  action: NowAction | null;
}

export interface NowPanelInput {
  event: SourcingEventV2Dto;
  now: number;
  unansweredQuestions: number;
  participationRequests: BuyerParticipationRequestsDto | null;
  bids: ReceivedBidV2Dto[];
  evaluation: EvaluationDto | null;
  rounds: readonly NegotiationRoundV2Dto[];
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const HIDDEN_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Draft,
  SourcingEventStatus.PublicationPending,
  SourcingEventStatus.PublicationFailed,
]);

const tab = (target: EventTab): NowActionTarget => ({ kind: "tab", tab: target });

const TERMINAL_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Cancelled,
  SourcingEventStatus.Closed,
]);

/** False where the panel renders nothing or a terminal line, so callers can skip fetching bids. */
export const panelReadsBids = (status: SourcingEventStatus): boolean =>
  !HIDDEN_STATUSES.has(status) && !TERMINAL_STATUSES.has(status);

const publicProcedureOf = (event: SourcingEventV2Dto) =>
  event.eventContext.type === "Public" ? event.eventContext.procedureType : null;

const hasPassed = (iso: string | null | undefined, now: number): boolean =>
  iso != null && now >= Date.parse(iso);

// A revoked award leaves `award` non-null but drops `awardedAt`, so presence alone would report a
// withdrawn decision as a confirmed one (SourcingEventV2Dto.toDto, the revokedDecisions branch).
const isAwarded = (event: SourcingEventV2Dto): boolean => event.award?.awardedAt != null;

const registeredInterest = (event: SourcingEventV2Dto): TranslationKey[] => {
  const count = (event.supplierRegistrations ?? []).length;
  return count > 0 ? [{ key: "{{count}} companies have registered interest", params: { count } }] : [];
};

const unansweredDetail = (count: number): TranslationKey[] =>
  count > 0 ? [{ key: "{{count}} unanswered questions", params: { count } }] : [];

const answerQuestions = (count: number): NowAction | null =>
  count > 0
    ? { labelKey: "Answer questions", target: { kind: "tab", tab: "communication", subTab: "questions" } }
    : null;

const openCompetition = (
  key: NowStateKey,
  deadlineIso: string | null,
  detail: TranslationKey[],
  action: NowAction | null,
): NowPanelState => ({
  key,
  status: deadlineIso
    ? { key: "The competition is open — deadline {{date}}", dateIso: deadlineIso }
    : { key: "The competition is open" },
  detail,
  progress: null,
  action,
});

const resumeLabel = (progress: NowProgress, startKey: string, continueKey: string): string =>
  progress.done === 0 ? startKey : continueKey;

const tendersOf = (bids: ReceivedBidV2Dto[]): ReceivedBidV2Dto[] =>
  bids.filter((bid) => bid.forNegotiationRound == null);

const isFullyEvaluated = (bid: EvalBidDto): boolean =>
  bid.cells.filter((cell) => !cell.isAutoComputed).every((cell) => cell.points != null);

export const deriveNowPanel = (input: NowPanelInput): NowPanelState | null => {
  const { event } = input;
  if (HIDDEN_STATUSES.has(event.status)) return null;

  switch (event.status) {
    case SourcingEventStatus.Cancelled:
      return terminal("CANCELLED", "This procurement has been cancelled");
    case SourcingEventStatus.Closed:
      return terminal("CLOSED", "This procurement is closed");
    case SourcingEventStatus.Published:
      return published(input);
    case SourcingEventStatus.Evaluation:
    case SourcingEventStatus.Completed:
      return evaluating(input);
    case SourcingEventStatus.AwardDecision:
      return awardDecision(input);
    case SourcingEventStatus.Awarded:
      return awardConfirmed(event, input.bids);
    default:
      return null;
  }
};

const terminal = (key: NowStateKey, statusKey: string): NowPanelState => ({
  key,
  status: { key: statusKey },
  detail: [],
  progress: null,
  action: null,
});

const published = (input: NowPanelInput): NowPanelState | null => {
  const { event, now, unansweredQuestions, participationRequests, bids } = input;
  const procedureType = publicProcedureOf(event);

  if (isSchemeProcedure(procedureType)) return schemeIntake(input);

  if (isTwoStageProcedure(procedureType)) {
    switch (event.competitionStage) {
      case CompetitionStageDto.RequestsOpen:
        return openCompetition(
          "REQUESTS_OPEN",
          effectiveRequestDeadline(event),
          [
            { key: "{{count}} requests received", params: { count: participationRequests?.count ?? 0 } },
            ...unansweredDetail(unansweredQuestions),
          ],
          answerQuestions(unansweredQuestions),
        );
      case CompetitionStageDto.Qualification:
        return requestQualification(input);
      default:
        break;
    }
  }

  if (!hasPassed(effectiveBidDeadline(event), now)) {
    return openCompetition(
      "TENDER_OPEN",
      effectiveBidDeadline(event),
      [...registeredInterest(event), ...unansweredDetail(unansweredQuestions)],
      answerQuestions(unansweredQuestions),
    );
  }

  if (isEvaluationLocked(event)) {
    const summary = deriveTenderQualification(event.eventData.selectionCriteria ?? [], tendersOf(bids));
    const progress = { done: summary.qualified + summary.rejected, total: summary.rows.length };
    return {
      key: "TENDER_QUALIFICATION",
      status: {
        key: "{{resolved}} of {{total}} suppliers qualified or rejected",
        params: { resolved: progress.done, total: progress.total },
      },
      detail: [],
      progress,
      action: {
        labelKey: resumeLabel(
          progress,
          "Start the qualification review",
          "Continue the qualification review",
        ),
        target: tab("qualification"),
      },
    };
  }

  return {
    key: "START_EVALUATION",
    status: { key: "The deadline has passed — ready to start the evaluation" },
    detail: [],
    progress: null,
    action: { labelKey: "Begin evaluation", target: tab("evaluation") },
  };
};

const schemeValidTo = (event: SourcingEventV2Dto): string | null => {
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  return publicContext?.dpsScheme?.validTo ?? publicContext?.qualificationScheme?.validTo ?? null;
};

// Forsyningsforskriften § 13-2 clocks admission in calendar months, not in virkedager.
const shortestAssessmentWindow = (
  event: SourcingEventV2Dto,
  awaiting: BuyerParticipationRequestDto[],
): number | null =>
  isQualificationScheme(publicProcedureOf(event))
    ? null
    : awaiting.reduce<number | null>((fewest, request) => {
      const remaining = request.assessmentWorkingDaysRemaining;
      if (remaining == null) return fewest;
      return fewest == null || remaining < fewest ? remaining : fewest;
    }, null);

const schemeIntake = (input: NowPanelInput): NowPanelState => {
  const { event, now, participationRequests } = input;
  const awaiting = (participationRequests?.requests ?? []).filter(
    (request) => request.status === ParticipationRequestStatus.Submitted,
  );

  if (awaiting.length === 0) {
    const expired = hasPassed(schemeValidTo(event), now);
    return {
      key: "SCHEME_INTAKE",
      status: { key: expired ? "The system has expired" : "The system is open for applications" },
      detail: [],
      progress: null,
      action: null,
    };
  }

  const shortest = shortestAssessmentWindow(event, awaiting);
  return {
    key: "SCHEME_INTAKE",
    status: { key: "{{count}} applications awaiting assessment", params: { count: awaiting.length } },
    detail:
      shortest == null
        ? []
        : [{ key: "Shortest deadline: {{count}} working days (FOA § 26-6)", params: { count: shortest } }],
    progress: null,
    action: { labelKey: "Assess applications", target: tab("dpsSuppliers") },
  };
};

const requestQualification = (input: NowPanelInput): NowPanelState => {
  const requests = input.participationRequests?.requests ?? [];
  const unresolved = requests.filter(
    (request) => request.status === ParticipationRequestStatus.Submitted,
  ).length;
  const progress = { done: requests.length - unresolved, total: requests.length };

  if (unresolved === 0 && requests.length > 0) {
    return {
      key: "REQUEST_SELECTION",
      status: { key: "Every request is reviewed — choose who to invite" },
      detail: [],
      progress: null,
      action: { labelKey: "Go to the qualification", target: tab("qualification") },
    };
  }

  return {
    key: "REQUEST_QUALIFICATION",
    status: {
      key: "{{resolved}} of {{total}} suppliers qualified or rejected",
      params: { resolved: progress.done, total: progress.total },
    },
    detail: [],
    progress,
    action: {
      labelKey: resumeLabel(progress, "Start the qualification review", "Continue the qualification review"),
      target: tab("qualification"),
    },
  };
};

const evaluating = (input: NowPanelInput): NowPanelState => {
  const { event, evaluation, rounds } = input;

  if (isAwarded(event)) return awardConfirmed(event, input.bids);

  const minimumRequirements = minimumRequirementsProgress(input);
  if (minimumRequirements != null) {
    return {
      key: "MINIMUM_REQUIREMENTS",
      status: {
        key: "{{reviewed}} of {{total}} tenders assessed against the minimum requirements",
        params: { reviewed: minimumRequirements.done, total: minimumRequirements.total },
      },
      detail: [],
      progress: minimumRequirements,
      action: {
        labelKey: resumeLabel(
          minimumRequirements,
          "Start the minimum requirements review",
          "Continue the minimum requirements review",
        ),
        target: tab("minimumRequirements"),
      },
    };
  }

  if (canSelectWinner(event.status, rounds)) {
    return {
      key: "READY_TO_AWARD",
      status: { key: "The evaluation is complete — ready for award" },
      detail: [],
      progress: null,
      action: { labelKey: "Select a winner", target: tab("award") },
    };
  }

  const rankable = (evaluation?.bids ?? []).filter((bid) => bid.status !== BidV2Status.Rejected);
  const progress = { done: rankable.filter(isFullyEvaluated).length, total: rankable.length };
  return {
    key: "EVALUATION",
    status: {
      key: "{{reviewed}} of {{total}} tenders fully evaluated",
      params: { reviewed: progress.done, total: progress.total },
    },
    detail: [],
    progress: progress.total > 0 ? progress : null,
    action: {
      labelKey: resumeLabel(progress, "Start the evaluation review", "Continue the evaluation review"),
      target: tab("evaluation"),
    },
  };
};

const minimumRequirementsProgress = (input: NowPanelInput): NowProgress | null => {
  const requirements = input.event.eventData.minimumRequirements ?? [];
  if (requirements.length === 0) return null;
  const summary = deriveMinimumRequirementReview(requirements, tendersOf(input.bids));
  if (summary.rows.length === 0 || summary.isResolved) return null;
  return { done: summary.rows.length - summary.pending, total: summary.rows.length };
};

const awardDecision = (input: NowPanelInput): NowPanelState => {
  const expiresAt = input.event.award?.standstillExpiresAt;

  if (expiresAt == null) {
    return {
      key: "AWARD_LETTERS_PENDING",
      status: { key: "Send the award letters to start the standstill period" },
      detail: [],
      progress: null,
      action: { labelKey: "See the award", target: tab("award") },
    };
  }

  if (hasPassed(expiresAt, input.now)) {
    return {
      key: "STANDSTILL_EXPIRED",
      status: { key: "The standstill period has expired" },
      detail: [],
      progress: null,
      action: { labelKey: "Confirm award", target: tab("award") },
    };
  }

  return {
    key: "STANDSTILL_RUNNING",
    status: {
      key: "The standstill period ends {{date}} ({{count}} days remaining)",
      params: { count: Math.max(0, Math.ceil((Date.parse(expiresAt) - input.now) / MS_PER_DAY)) },
      dateIso: expiresAt,
    },
    detail: [],
    progress: null,
    action: { labelKey: "See the award", target: tab("award") },
  };
};

const awardConfirmed = (event: SourcingEventV2Dto, bids: ReceivedBidV2Dto[]): NowPanelState => {
  const contractIds = event.award?.contractIds ?? [];
  const onlyContract = contractIds.length === 1
    ? bids.find((bid) => bid.contractId === contractIds[0])
    : undefined;
  return {
    key: "AWARD_CONFIRMED",
    status: { key: "The award is confirmed" },
    detail: [],
    progress: null,
    action:
      contractIds.length === 0
        ? { labelKey: "Create contract", target: tab("award") }
        : {
          labelKey: "Go to contract",
          // contractUrl routes on the contract's state, which only the bid carries.
          target: onlyContract
            ? { kind: "url", url: contractUrl(contractIds[0], onlyContract.contractState, event.id) }
            : tab("award"),
        },
  };
};
